import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import { UploadProvider, useUpload } from "@/context/UploadContext";
import { AppErrorBoundary } from "@/components/shared/AppErrorBoundary";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import VideoDetailPage from "@/pages/VideoDetailPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import PricingPage from "@/pages/PricingPage";
import BillingPage from "@/pages/BillingPage";
import MercadoPagoSuccessPage from "@/pages/MercadoPagoSuccessPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import { bootstrapClientErrorLogging, updateErrorLoggingContext } from "@/lib/errorLogging";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirect if logged in)
const PublicRoute = ({ children, allowAuthenticated = false }) => {
  const { user, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user && !allowAuthenticated) {
    return <Navigate to={isAdmin ? "/admin" : "/dashboard"} replace />;
  }
  
  return children;
};

function AppRoutes() {
  const location = useLocation();
  const { user } = useAuth();

  React.useEffect(() => {
    bootstrapClientErrorLogging();
  }, []);

  React.useEffect(() => {
    updateErrorLoggingContext({
      routePath: `${location.pathname}${location.search}`,
      userEmail: user?.email || null,
      userId: user?.id || null,
    });
  }, [location.pathname, location.search, user?.email, user?.id]);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute allowAuthenticated><ResetPasswordPage /></PublicRoute>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/terms" element={<PublicRoute allowAuthenticated><TermsPage /></PublicRoute>} />
      <Route path="/privacy" element={<PublicRoute allowAuthenticated><PrivacyPolicyPage /></PublicRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/checkout/mercadopago/success" element={<ProtectedRoute><MercadoPagoSuccessPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/video/:videoId" element={<ProtectedRoute><VideoDetailPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <UploadProvider>
            <AppErrorBoundary>
              <div className="App min-h-screen bg-background">
                <AppRoutes />
                <Toaster position="top-right" richColors />
              </div>
            </AppErrorBoundary>
          </UploadProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
